import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useValidationMode } from '@/features/pins';
import { useFiltersStore, isFilterActive } from '@/features/filters';
import { useChoroplethStore } from './store';
import { LAYER_GROUPS, LAYERS_BY_ID } from './layers-config';
import { useAvailableLayers } from './useAvailableLayers';
import { Legend } from './Legend';
import { useLocaleSuggestion, dismissSuggestion } from '@/hooks/useLocaleSuggestions';

const GROUP_ORDER = ['climate', 'economy', 'tax', 'infra', 'demo'] as const;

// Раньше здесь было захардкоженное множество из одного элемента, и это
// работало ровно до второй страны: каталог слоёв общий на весь продукт, а
// собирает каждая страна своё. Теперь пустые слои определяются по данным
// (useAvailableLayers) и в список не попадают вовсе — кроме выбранного,
// который остаётся видимым с пометкой, иначе селект потерял бы значение.

export function LayerSwitcher() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const {
    activeLayer,
    setActiveLayer,
    mode,
    setMode,
    overlayHidden,
    toggleOverlayHidden,
    terrainVisible,
    toggleTerrainVisible,
  } = useChoroplethStore();
  const validation = useValidationMode();
  const localeSuggestion = useLocaleSuggestion();
  const { applied: appliedFilters, reset: resetFilters } = useFiltersStore();
  const filtersActive = isFilterActive(appliedFilters);

  // Пока датасет не загружен, множество пустое — тогда показываем каталог
  // целиком, как было раньше: лучше лишний пункт, чем пустой переключатель.
  const available = useAvailableLayers();
  const isEmpty = (id: string) => available.size > 0 && !available.has(id);
  const isVisible = (id: string) => id === activeLayer || !isEmpty(id);
  const [hintDismissed, setHintDismissed] = useState(localeSuggestion?.isDismissed ?? true);
  const showHint =
    !hintDismissed &&
    localeSuggestion &&
    !localeSuggestion.isDismissed &&
    localeSuggestion.layerId !== null &&
    activeLayer !== localeSuggestion.layerId;

  // Esc закрывает на мобиле
  useEffect(() => {
    if (!isMobile || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, open]);

  const activeLayerDef = LAYERS_BY_ID[activeLayer];
  const activeLabel = activeLayerDef
    ? t(`layer.label.${activeLayer}`, { defaultValue: activeLayerDef.label })
    : t('layer.title');

  // US-07: единый radio из трёх опций вместо двух тогглов (mode + overlayHidden).
  // «Только карта» = overlayHidden:true, но текущий mode сохраняем — так пользователь
  // может вернуться в свой режим одним кликом.
  type DataMode = 'choropleth' | 'heatmap' | 'none';
  const dataMode: DataMode = overlayHidden ? 'none' : mode;
  const setDataMode = (m: DataMode) => {
    if (m === 'none') {
      if (!overlayHidden) toggleOverlayHidden();
    } else {
      if (overlayHidden) toggleOverlayHidden();
      setMode(m);
    }
  };

  // Desktop: card с mode toggle + select + inline legend
  if (!isMobile) {
    return (
      <div
        data-tour="layer"
        className="absolute left-4 top-16 z-[1000] flex w-72 flex-col gap-2 rounded-lg bg-white/95 p-2 shadow-md backdrop-blur-sm"
      >
        {/* US-07: three-way radio (data layer / heatmap / map-only) */}
        <div role="radiogroup" aria-label={t('layer.mode.label', { defaultValue: 'Display mode' })}
             className="flex rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
          <button
            role="radio"
            onClick={() => setDataMode('choropleth')}
            className={`flex min-h-[32px] flex-1 items-center justify-center rounded px-2 py-1 transition ${
              dataMode === 'choropleth'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            aria-checked={dataMode === 'choropleth'}
          >
            🧩 {t('layer.mode.choropleth')}
          </button>
          <button
            role="radio"
            onClick={() => setDataMode('heatmap')}
            className={`flex min-h-[32px] flex-1 items-center justify-center rounded px-2 py-1 transition ${
              dataMode === 'heatmap'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            aria-checked={dataMode === 'heatmap'}
          >
            🌈 {t('layer.mode.heatmap')}
          </button>
          <button
            role="radio"
            onClick={() => setDataMode('none')}
            className={`flex min-h-[32px] flex-1 items-center justify-center rounded px-2 py-1 transition ${
              dataMode === 'none'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
            aria-checked={dataMode === 'none'}
            title={t('layer.mode.noneHint', {
              defaultValue: 'Hide the colour overlay — show just the basemap. Triggers automatically at zoom 15+.',
            })}
          >
            🗺 {t('layer.mode.none', { defaultValue: 'Map only' })}
          </button>
        </div>

        {/* Layer select */}
        <div className="relative">
          <label htmlFor="layer-select" className="sr-only">
            {t('layer.title')}
          </label>
          <select
            id="layer-select"
            className="w-full min-h-[36px] cursor-pointer appearance-none rounded-md border border-slate-300 bg-white px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={activeLayer}
            onChange={(e) => setActiveLayer(e.target.value)}
          >
            {GROUP_ORDER.map((group) => {
              const groupLayers = (LAYER_GROUPS[group] ?? []).filter(
                (l) => isVisible(l.id),
              );
              if (!groupLayers.length) return null;
              return (
                <optgroup key={group} label={t(`layer.group.${group}`)}>
                  {groupLayers.map((l) => (
                    <option key={l.id} value={l.id}>
                      {t(`layer.label.${l.id}`, { defaultValue: l.label })}
                      {isEmpty(l.id) ? ` ·· ${t('layer.noData')}` : ''}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-500"
          >
            ▼
          </span>
        </div>

        <Legend />

        {/* US-07: addon — рельеф (независимо от mode) */}
        <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-[11px] text-slate-700 hover:bg-slate-50">
          <input
            type="checkbox"
            checked={terrainVisible}
            onChange={toggleTerrainVisible}
            className="cursor-pointer"
          />
          <span aria-hidden="true">⛰️</span>
          <span>{t('layer.addon.terrain', { defaultValue: 'Terrain' })}</span>
        </label>

        {/* US-10: validation-mode indicator — show only when user has ≥2 own pins. */}
        {validation.active && (
          <div className="flex items-center justify-between gap-2 rounded-md bg-[color:var(--color-paper-2)] px-2 py-1.5 text-[11px] text-[color:var(--color-ink-700)]">
            <span>
              📌 {t('pins.validation.indicator', {
                count: validation.count,
                defaultValue: 'У вас {{count}} мест на карте',
              })}
            </span>
            <Link
              to="/it/compare"
              className="font-semibold text-[color:var(--color-terra-600)] hover:underline"
            >
              {t('pins.validation.compare', { defaultValue: 'Сравнить →' })}
            </Link>
          </div>
        )}

        {/* Locale-based layer suggestion — not auto-activated, just highlighted */}
        {showHint && localeSuggestion?.layerId && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800">
            <span className="shrink-0">✨</span>
            <span className="flex-1">
              {t(localeSuggestion.hintKey ?? '', {
                defaultValue: 'A layer relevant to your language is available.',
              })}
            </span>
            <div className="flex shrink-0 gap-1">
              <button
                onClick={() => {
                  setActiveLayer(localeSuggestion.layerId!);
                  setHintDismissed(true);
                  dismissSuggestion(i18n.language?.split('-')[0] ?? '');
                }}
                className="font-semibold underline underline-offset-1 hover:no-underline"
              >
                {t('locale.show')}
              </button>
              <button
                onClick={() => {
                  setHintDismissed(true);
                  dismissSuggestion(i18n.language?.split('-')[0] ?? '');
                }}
                className="text-amber-600 hover:text-amber-900"
                title={t('close', { defaultValue: 'Close' })}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Filter-active notice. When data layer is hidden ('Только карта') but
            filters are active, the user can't SEE the effect of their filters →
            promote the notice to amber and offer a one-click way to re-enable
            the data layer. When the data layer is on, keep the calm blue style. */}
        {filtersActive && (
          dataMode === 'none' ? (
            <div className="flex flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-2 text-[11px] text-amber-900">
              <span className="font-semibold">
                ⚠ {t('layer.filterActiveHidden', {
                  defaultValue: 'Filters are active but the data layer is hidden — you cannot see what matches.',
                })}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setDataMode('choropleth')}
                  className="rounded-full bg-amber-700 px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-amber-800"
                >
                  {t('layer.showLayer', { defaultValue: 'Show data layer' })}
                </button>
                <button
                  onClick={resetFilters}
                  className="rounded-full border border-amber-700 px-2.5 py-1 text-[10px] font-semibold text-amber-900 hover:bg-amber-100"
                >
                  {t('filters.reset')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-md border border-blue-100 bg-blue-50 px-2 py-1.5 text-[11px] text-blue-800">
              <span>🔍 {t('layer.filterActive')}</span>
              <button
                onClick={resetFilters}
                className="shrink-0 font-semibold underline underline-offset-1 hover:no-underline"
              >
                {t('filters.reset')}
              </button>
            </div>
          )
        )}
      </div>
    );
  }

  // Mobile collapsed: FAB
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="absolute right-2 top-16 z-[1000] inline-flex min-h-[40px] max-w-[55%] items-center gap-1 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-md backdrop-blur-sm hover:bg-white sm:right-4 sm:top-4"
        aria-label={t('layer.openSheet')}
        title={t('layer.title')}
      >
        <span aria-hidden="true">{mode === 'heatmap' ? '🌈' : '🧩'}</span>
        <span className="truncate">{activeLabel}</span>
      </button>
    );
  }

  // Mobile open: bottom sheet
  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className="fixed inset-0 z-[1009] bg-black/30"
        aria-hidden="true"
      />
      <aside className="absolute inset-x-0 bottom-0 z-[1010] flex max-h-[80dvh] flex-col overflow-hidden rounded-t-2xl bg-white/95 shadow-2xl backdrop-blur-sm pb-safe">
        <div className="flex justify-center py-1.5" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm font-semibold text-slate-800">{t('layer.title')}</span>
          <button
            onClick={() => setOpen(false)}
            className="flex h-10 w-10 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            aria-label={t('details.closeTooltip')}
            title={t('details.closeTooltip')}
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto overscroll-contain p-3">
          {/* US-07: 3-way radio */}
          <div role="radiogroup" aria-label={t('layer.mode.label', { defaultValue: 'Display mode' })}
               className="inline-flex w-full rounded-md bg-slate-100 p-0.5 text-sm font-medium">
            <button
              role="radio"
              onClick={() => setDataMode('choropleth')}
              className={`min-h-[36px] flex-1 rounded px-2 py-1 transition ${
                dataMode === 'choropleth' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
              aria-checked={dataMode === 'choropleth'}
            >
              🧩 {t('layer.mode.choropleth')}
            </button>
            <button
              role="radio"
              onClick={() => setDataMode('heatmap')}
              className={`min-h-[36px] flex-1 rounded px-2 py-1 transition ${
                dataMode === 'heatmap' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
              aria-checked={dataMode === 'heatmap'}
            >
              🌈 {t('layer.mode.heatmap')}
            </button>
            <button
              role="radio"
              onClick={() => setDataMode('none')}
              className={`min-h-[36px] flex-1 rounded px-2 py-1 transition ${
                dataMode === 'none' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
              aria-checked={dataMode === 'none'}
            >
              🗺 {t('layer.mode.none', { defaultValue: 'Только карта' })}
            </button>
          </div>

          {/* Addon: terrain */}
          <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={terrainVisible}
              onChange={toggleTerrainVisible}
              className="cursor-pointer"
            />
            <span aria-hidden="true">⛰️</span>
            <span>{t('layer.addon.terrain', { defaultValue: 'Terrain' })}</span>
          </label>

          {/* Layer list */}
          <div className="flex flex-col gap-2">
            {GROUP_ORDER.map((group) => {
              const groupLayers = (LAYER_GROUPS[group] ?? []).filter(
                (l) => isVisible(l.id),
              );
              if (!groupLayers.length) return null;
              return (
                <div key={group}>
                  <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {t(`layer.group.${group}`)}
                  </div>
                  <div className="flex flex-col">
                    {groupLayers.map((l) => {
                      const isActive = activeLayer === l.id;
                      const noData = isEmpty(l.id);
                      return (
                        <button
                          key={l.id}
                          onClick={() => {
                            setActiveLayer(l.id);
                            setOpen(false);
                          }}
                          className={`flex min-h-[40px] items-center justify-between rounded px-2 py-1.5 text-sm ${
                            isActive
                              ? 'bg-blue-50 font-semibold text-blue-700'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span aria-hidden="true">{isActive ? '●' : '○'}</span>
                            {t(`layer.label.${l.id}`, { defaultValue: l.label })}
                          </span>
                          {noData && (
                            <span className="text-[10px] text-slate-400">
                              {t('layer.noData')}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </aside>
    </>
  );
}
